import React from 'react';
import { X, Eye, Repeat, Weight, Clock, Layers, Info } from 'lucide-react';
import { ExerciseTemplate, ExerciseTemplateExercise } from '../types';

interface ViewTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: ExerciseTemplate | null;
}

const ViewTemplateModal: React.FC<ViewTemplateModalProps> = ({
  isOpen,
  onClose,
  template
}) => {
  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Eye className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {template.name}
              </h2>
              <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                <span>
                  Всего упражнений: <span className="font-medium">{template.template_exercises?.length || 0}</span>
                </span>
                <span>
                  Создан: {new Date(template.created_at).toLocaleDateString('ru-RU')}
                </span>
              </div>
              {template.description && (
                <p className="text-sm text-gray-600 mt-1">
                  {template.description}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Содержимое */}
        <div 
          className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]" 
          style={{ 
            transform: 'translateZ(0)',
            willChange: 'scroll-position',
            backfaceVisibility: 'hidden',
            overflowX: 'hidden'
          }}
        >
          {template.template_exercises && template.template_exercises.length > 0 ? (
            <div className="space-y-2">
              {template.template_exercises
                .sort((a, b) => a.order_index - b.order_index)
                .map((templateExercise: ExerciseTemplateExercise, index: number) => (
                  <div
                    key={templateExercise.id}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                    style={{ 
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full mr-2">
                            {index + 1}
                          </span>
                          <h3 className="text-base font-medium text-gray-900">
                            {templateExercise.exercise?.name || 'Упражнение удалено'}
                          </h3>
                        </div>

                        {/* Параметры упражнения */}
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center text-sm">
                            <Layers className="w-4 h-4 text-gray-500 mr-1" />
                            <span className="font-medium">{templateExercise.sets}</span>
                          </div>
                          
                          <div className="flex items-center text-sm">
                            <Repeat className="w-4 h-4 text-gray-500 mr-1" />
                            <span className="font-medium">{templateExercise.reps}</span>
                          </div>
                          
                          {templateExercise.weight && (
                            <div className="flex items-center text-sm">
                              <Weight className="w-4 h-4 text-gray-500 mr-1" />
                              <span className="font-medium">{templateExercise.weight} кг</span>
                            </div>
                          )}
                        </div>

                        {/* Заметки */}
                        {templateExercise.notes && (
                          <div className="mt-2 p-2 bg-blue-50 rounded-md">
                            <div className="flex items-start">
                              <Info className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-blue-800">
                                {templateExercise.notes}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Группы мышц */}
                        {templateExercise.exercise?.muscle_groups && templateExercise.exercise.muscle_groups.length > 0 && (
                          <div className="mt-2">
                            <div className="flex flex-wrap gap-1">
                              {templateExercise.exercise.muscle_groups.map((muscleGroup) => (
                                <span
                                  key={muscleGroup.id}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {muscleGroup.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                <Eye className="h-12 w-12" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Нет упражнений
              </h3>
              <p className="text-gray-500">
                В этом шаблоне пока нет упражнений
              </p>
            </div>
          )}
        </div>

        {/* Футер */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          {/* Футер оставлен пустым для возможного добавления кнопок в будущем */}
        </div>
      </div>
    </div>
  );
};

export default ViewTemplateModal; 